/**
 * useOutbox — envia a fila de cadastros quando há rede.
 *
 * O risco desta funcionalidade é duplicar pessoas: o envio chega ao servidor,
 * a resposta perde-se no caminho, e a retentativa cria um segundo perfil.
 *
 * A protecção não é um identificador de idempotência inventado aqui — é o
 * próprio servidor, que recusa telefones repetidos com 409. Por isso um 409
 * na retentativa significa "já lá está": tira-se da fila e conta como
 * enviado, em vez de ficar a repetir para sempre.
 *
 * O mesmo para 4xx em geral: um cadastro que o servidor recusa por ser
 * inválido nunca vai passar à décima tentativa. Fica marcado com o erro para
 * o agente ver, e sai da rotação automática.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { lerFila, removerDaFila, actualizarNaFila } from '../utils/offline';

const MAX_TENTATIVAS = 5;

export function useOutbox() {
  const [fila, setFila] = useState([]);
  const [aSincronizar, setASincronizar] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const aCorrer = useRef(false);

  const recarregar = useCallback(async () => {
    setFila(await lerFila());
  }, []);

  const enviarUm = useCallback(async (item) => {
    // 1) Criar o trabalhador
    let worker = item.worker_id ? { user_id: item.worker_id, worker_code: item.worker_code } : null;
    if (!worker) {
      const { data } = await api.post('/operations/workers', item.dados);
      worker = data;
      // Guarda já o id: se as fotografias falharem a seguir, a retentativa
      // não volta a criar o trabalhador.
      await actualizarNaFila({ ...item, worker_id: data.user_id, worker_code: data.worker_code });
    }

    // 2) Documentos, se os houver
    if (item.bi_number && item.fotos?.bi_photo && item.fotos?.profile_photo) {
      const fd = new FormData();
      fd.append('bi_number', item.bi_number);
      Object.entries(item.fotos).forEach(([campo, blob]) => {
        if (blob) fd.append(campo, blob, `${campo}.jpg`);
      });
      await api.post(`/verification/worker/${worker.user_id}`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
    }

    return worker;
  }, []);

  const sincronizar = useCallback(async ({ manual = false } = {}) => {
    if (aCorrer.current || !navigator.onLine) return;
    const pendentes = await lerFila();
    const aEnviar = pendentes.filter((i) => manual || i.tentativas < MAX_TENTATIVAS);
    if (!aEnviar.length) { setFila(pendentes); return; }

    aCorrer.current = true;
    setASincronizar(true);
    try {
      for (const item of aEnviar) {
        try {
          await enviarUm(item);
          await removerDaFila(item.id);
        } catch (e) {
          const status = e.response?.status;
          const msg = e.response?.data?.message || e.message;

          // 409 = o servidor já o tem. Missão cumprida.
          if (status === 409) {
            await removerDaFila(item.id);
            continue;
          }
          // Recusa definitiva: não vale a pena insistir.
          if (status >= 400 && status < 500) {
            await actualizarNaFila({ ...item, tentativas: MAX_TENTATIVAS, ultimo_erro: msg, definitivo: true });
            continue;
          }
          // Rede ou servidor: tenta outra vez mais tarde.
          await actualizarNaFila({ ...item, tentativas: (item.tentativas || 0) + 1, ultimo_erro: msg });
          if (!navigator.onLine) break;
        }
      }
    } finally {
      aCorrer.current = false;
      setASincronizar(false);
      await recarregar();
    }
  }, [enviarUm, recarregar]);

  useEffect(() => {
    recarregar();
    const voltou = () => { setOnline(true); sincronizar(); };
    const caiu = () => setOnline(false);
    window.addEventListener('online', voltou);
    window.addEventListener('offline', caiu);
    // Tentativa periódica: a rede pode voltar sem o evento 'online' disparar
    // (acontece em ligações móveis fracas, que é o caso do Zango).
    const t = setInterval(() => { if (navigator.onLine) sincronizar(); }, 45000);
    if (navigator.onLine) sincronizar();

    // O service worker acorda quando a rede volta, mesmo com a app fechada, e
    // manda esvaziar a fila. Sem isto, o cadastro ficava no telefone ate
    // alguem reabrir a app — e quem esta no terreno fecha-a assim que acaba o
    // atendimento.
    //
    // O envio continua a ser feito aqui e nao dentro do service worker: a fila
    // tem fotografias, autenticacao e regras de negocio, e duplicar isso la
    // dentro criava duas versoes da mesma logica que divergiam na primeira
    // alteracao.
    const doSW = (ev) => { if (ev.data?.tipo === 'ESVAZIAR_FILA') sincronizar(); };
    navigator.serviceWorker?.addEventListener?.('message', doSW);

    // Pedir ao browser que avise. `sync` so existe no Chrome/Android; onde nao
    // existe, ficam o evento 'online' e o temporizador, que e o que ja havia.
    navigator.serviceWorker?.ready
      ?.then((reg) => reg.sync?.register('dozy-fila-cadastros'))
      ?.catch(() => { /* sem Background Sync: nao se perde nada, so automatismo */ });
    return () => {
      window.removeEventListener('online', voltou);
      window.removeEventListener('offline', caiu);
      navigator.serviceWorker?.removeEventListener?.('message', doSW);
      clearInterval(t);
    };
  }, [recarregar, sincronizar]);

  return {
    fila,
    pendentes: fila.filter((i) => !i.definitivo).length,
    comErro: fila.filter((i) => i.definitivo).length,
    aSincronizar,
    online,
    sincronizar,
    recarregar,
    descartar: async (id) => { await removerDaFila(id); await recarregar(); },
  };
}
